import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Shell from "../components/Shell";
import { statusMeta } from "../lib/status";
import { STREET_IMG } from "../lib/brand";
import { ProfileImageField } from "../components/ProfileImageField";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Loader2, ChevronRight, Home, Clock, Ban, Users, Send, Star, Plus, Trash2, CheckCircle2, Check, Phone, MessageSquare, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const inputCls = "bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-[#ff3b30]";

/* Call / Text / Email quick actions */
function ContactRow({ phone, email, testidPrefix = "contact" }) {
  if (!phone && !email) return null;
  const base = "flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-semibold transition-colors";
  const ghost = "bg-white/[0.06] text-white border border-white/15 hover:bg-white/[0.12]";
  return (
    <div className="flex gap-2 mt-3" data-testid={`${testidPrefix}-row`}>
      {phone && (
        <a href={`tel:${phone}`} data-testid={`${testidPrefix}-call`} className={`${base} bg-[#ff3b30] text-white hover:bg-[#ff5147]`}>
          <Phone className="h-3.5 w-3.5" /> Call
        </a>
      )}
      {phone && (
        <a href={`sms:${phone}`} data-testid={`${testidPrefix}-text`} className={`${base} ${ghost}`}>
          <MessageSquare className="h-3.5 w-3.5" /> Text
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} data-testid={`${testidPrefix}-email`} className={`${base} ${ghost}`}>
          <Mail className="h-3.5 w-3.5" /> Email
        </a>
      )}
    </div>
  );
}

/* Small circular lender avatar — shows their headshot, falls back to an initial */
function LenderAvatar({ headshotPath, name, email }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let obj = null;
    if (headshotPath) {
      api.get(`/files/${headshotPath}`, { responseType: "blob" })
        .then((res) => { obj = URL.createObjectURL(res.data); setSrc(obj); })
        .catch(() => setSrc(null));
    } else {
      setSrc(null);
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [headshotPath]);

  const initial = (name || email || "?").trim()[0]?.toUpperCase() || "?";
  return (
    <div className="h-9 w-9 rounded-full overflow-hidden bg-[#ff3b30]/15 text-[#ff3b30] flex items-center justify-center font-display font-bold text-sm shrink-0 border border-white/10">
      {src ? <img src={src} alt={name || email} className="h-full w-full object-cover" data-testid="lender-avatar-img" /> : initial}
    </div>
  );
}

/* ---------------- My Clients ---------------- */
function ClientsTab() {
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  }, []);

  if (clients === null)
    return <Loader2 className="h-6 w-6 animate-spin text-[#ff3b30] mx-auto my-16" />;
  if (clients.length === 0)
    return (
      <div className="border border-white/10 rounded-2xl p-12 text-center" data-testid="no-clients">
        <Users className="h-8 w-8 mx-auto text-white/30" />
        <p className="mt-4 text-white/70 font-medium">No clients yet</p>
        <p className="text-sm text-white/45 mt-1 max-w-sm mx-auto">
          Once your lender loads a client and their loan estimate, they'll appear here ready for pre-approval.
        </p>
      </div>
    );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {clients.map((c, i) => (
        <button
          key={c.id}
          onClick={() => navigate(`/client/${c.id}`)}
          data-testid={`client-card-${c.id}`}
          style={{ animationDelay: `${i * 60}ms` }}
          className="mpl-rise text-left bg-[#141414]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:-translate-y-1 hover:border-white/30 hover:bg-[#141414]/90 transition-[transform,border-color,background-color] duration-200 group shadow-2xl shadow-black/40"
        >
          <div className="flex items-center justify-between">
            <div className="h-10 w-10 rounded-full bg-[#ff3b30]/15 text-[#ff3b30] flex items-center justify-center font-display font-bold">
              {c.borrower_name?.[0]?.toUpperCase() || "?"}
            </div>
            <ChevronRight className="h-5 w-5 text-white/30 group-hover:text-[#ff3b30] transition-colors" />
          </div>
          <p className="mt-4 font-display text-xl tracking-tight">{c.borrower_name}</p>
          <p className="text-sm text-white/45">{c.borrower_email}</p>
          <span
            className={`mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border ${statusMeta(c.status).cls}`}
            data-testid={`client-status-${c.id}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${statusMeta(c.status).dot}`} />
            {statusMeta(c.status).label}
          </span>
          <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 text-sm">
            <Home className="h-4 w-4 text-white/40" />
            <span className="text-white/60">Approved up to</span>
            <span className="ml-auto font-semibold mpl-tabular">{money(c.max_purchase_price)}</span>
          </div>
          {!c.loan_estimate_path && <p className="mt-3 text-xs text-[#ff3b30]">Awaiting loan estimate</p>}
        </button>
      ))}
    </div>
  );
}

/* ---------------- My Lenders ---------------- */
function MyLendersTab() {
  const [lenders, setLenders] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/agent/lenders").then((r) => setLenders(r.data)).catch(() => setLenders([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!email) return toast.error("Add the lender's email");
    setBusy(true);
    try {
      await api.post("/agent/lenders", { name, email });
      setName(""); setEmail("");
      await load();
      toast.success("Lender added 🍭");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add lender"); }
    finally { setBusy(false); }
  };
  const makePreferred = async (id) => { await api.patch(`/agent/lenders/${id}/preferred`); load(); toast.success("Preferred lender updated ⭐"); };
  const remove = async (id) => { await api.delete(`/agent/lenders/${id}`); load(); };
  const invite = async (id) => {
    try { await api.post(`/agent/lenders/${id}/invite`); toast.success("Invite sent — we CC'd you 🍭"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send invite"); }
  };

  if (lenders === null) return <Loader2 className="h-6 w-6 animate-spin text-[#ff3b30] mx-auto my-16" />;

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-white/55 mb-5">
        Save up to 10 lenders and ⭐ star your go-to. Not on My Pocket Lender yet? Send them an invite — we'll CC you.
      </p>

      <div className="space-y-2.5" data-testid="lenders-list">
        {lenders.length === 0 && (
          <div className="border border-white/10 rounded-2xl py-10 text-center text-white/45">
            No lenders yet — add your first below.
          </div>
        )}
        {lenders.map((l) => (
          <div key={l.id} data-testid={`lender-row-${l.id}`}
            className={`rounded-xl border p-3.5 ${l.preferred ? "border-[#ff3b30]/50 bg-[#ff3b30]/10" : "border-white/10 bg-black/30"}`}>
            <div className="flex items-center gap-3">
              <button onClick={() => makePreferred(l.id)} data-testid={`lender-prefer-${l.id}`} title="Set as preferred"
                className={l.preferred ? "text-[#ff3b30]" : "text-white/30 hover:text-white/70 transition-colors"}>
                <Star className={`h-5 w-5 ${l.preferred ? "fill-[#ff3b30]" : ""}`} />
              </button>
              <LenderAvatar headshotPath={l.headshot_path} name={l.name} email={l.email} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{l.name || l.email}</p>
                <p className="text-xs text-white/45 truncate">{l.email}</p>
              </div>
              {l.has_account ? (
                <span className="text-[10px] tracking-wide uppercase text-emerald-300 border border-emerald-300/40 bg-emerald-400/10 rounded-full px-2 py-0.5 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Connected
                </span>
              ) : (
                <button onClick={() => invite(l.id)} data-testid={`lender-invite-${l.id}`}
                  className="text-[11px] font-semibold text-[#ff3b30] border border-[#ff3b30]/40 rounded-full px-2.5 py-1 hover:bg-[#ff3b30]/10 transition-colors flex items-center gap-1">
                  <Send className="h-3 w-3" /> Invite
                </button>
              )}
              <button onClick={() => remove(l.id)} data-testid={`lender-remove-${l.id}`} className="text-white/30 hover:text-[#ff3b30] transition-colors">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <ContactRow phone={l.phone} email={l.email} testidPrefix={`lender-contact-${l.id}`} />
          </div>
        ))}
      </div>

      {lenders.length < 10 && (
        <div className="mt-5 pt-5 border-t border-white/10 space-y-2.5">
          <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Add a lender</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="add-lender-name"
              className={inputCls} placeholder="Name (optional)" />
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="add-lender-email"
              className={inputCls} placeholder="lender@email.com" />
          </div>
          <Button onClick={add} disabled={busy} data-testid="add-lender-submit"
            className="h-10 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-lg px-6">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus className="h-4 w-4 mr-1" /> Add lender</>)}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ---------------- Profile ---------------- */
function ProfileTab() {
  const { refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [logoPath, setLogoPath] = useState(null);
  const [headshotPath, setHeadshotPath] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [headshotBusy, setHeadshotBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const hydratedRef = useRef(false);

  useEffect(() => {
    api.get("/agent/profile").then((r) => {
      if (hydratedRef.current) return;
      hydratedRef.current = true;
      setProfile({
        name: r.data.name || "", email: r.data.email || "", phone: r.data.phone || "",
        company: r.data.company || "", license_number: r.data.license_number || "",
      });
      setLogoPath(r.data.logo_path || null);
      setHeadshotPath(r.data.headshot_path || null);
    });
  }, []);

  const set = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/agent/profile", {
        name: profile.name, phone: profile.phone, company: profile.company, license_number: profile.license_number,
      });
      await refresh();
      toast.success("Profile saved 🍭");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadImg = async (kind, f) => {
    if (!f) return;
    const setBusy = kind === "logo" ? setLogoBusy : setHeadshotBusy;
    const setPath = kind === "logo" ? setLogoPath : setHeadshotPath;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append(kind, f);
      const res = await api.post(`/agent/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPath(res.data[`${kind}_path`]);
      toast.success(`${kind === "logo" ? "Logo" : "Headshot"} updated`);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteImg = async (kind) => {
    const setBusy = kind === "logo" ? setLogoBusy : setHeadshotBusy;
    const setPath = kind === "logo" ? setLogoPath : setHeadshotPath;
    setBusy(true);
    try {
      await api.delete(`/agent/${kind}`);
      setPath(null);
    } catch (e) {
      toast.error("Could not remove");
    } finally {
      setBusy(false);
    }
  };

  if (!profile) return <Loader2 className="h-6 w-6 animate-spin text-[#ff3b30] mx-auto my-16" />;

  return (
    <form onSubmit={save} className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 rounded-2xl p-6 max-w-2xl space-y-5">
      <p className="text-sm text-white/50">Your details and photo so your buyers and lenders know exactly who they're working with.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Full name</Label>
          <Input className={inputCls} value={profile.name} onChange={set("name")} data-testid="agent-profile-name" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Email</Label>
          <Input className={`${inputCls} opacity-60`} value={profile.email} disabled data-testid="agent-profile-email" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Phone</Label>
          <Input className={inputCls} value={profile.phone} onChange={set("phone")} data-testid="agent-profile-phone" placeholder="(555) 555-5555" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Brokerage / Company</Label>
          <Input className={inputCls} value={profile.company} onChange={set("company")} data-testid="agent-profile-company" placeholder="Acme Realty" />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Realtor license #</Label>
          <Input className={inputCls} value={profile.license_number} onChange={set("license_number")} data-testid="agent-profile-license" placeholder="DRE / License #" />
        </div>
      </div>

      <ProfileImageField label="Your logo / brokerage logo" kind="logo" path={logoPath} busy={logoBusy}
        onUpload={(f) => uploadImg("logo", f)} onDelete={() => deleteImg("logo")} />
      <ProfileImageField label="Your headshot" kind="headshot" path={headshotPath} busy={headshotBusy}
        onUpload={(f) => uploadImg("headshot", f)} onDelete={() => deleteImg("headshot")} circle />

      <Button type="submit" disabled={saving} data-testid="agent-save-profile-button" className="h-11 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl px-8">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-2" /> Save profile</>}
      </Button>
    </form>
  );
}

/* ---------------- Banner ---------------- */
function Banner({ status, canSend, lender }) {
  if (status === "pending")
    return (
      <div className="flex items-start gap-3 bg-white text-black rounded-xl px-5 py-4 mb-8" data-testid="pending-banner">
        <Clock className="h-5 w-5 mt-0.5 text-[#ff3b30] shrink-0" />
        <div>
          <p className="font-semibold">Your account is pending approval.</p>
          <p className="text-sm text-black/70">{lender?.lender_name || "Your lender"} will approve you and load your clients shortly.</p>
        </div>
      </div>
    );
  if (!canSend)
    return (
      <div className="flex items-start gap-3 bg-white text-black rounded-xl px-5 py-4 mb-8" data-testid="disabled-banner">
        <Ban className="h-5 w-5 mt-0.5 text-[#ff3b30] shrink-0" />
        <div>
          <p className="font-semibold">Some items have been updated.</p>
          <p className="text-sm text-black/70">
            Pre-approvals are paused. Contact {lender?.lender_name || "your lender"}
            {lender?.lender_phone ? ` at ${lender.lender_phone}` : ""} to get more details.
          </p>
        </div>
      </div>
    );
  return null;
}

/* ---------------- Page ---------------- */
export default function AgentDashboard() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/agent/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  return (
    <Shell
      title={`Hi, ${user?.name?.split(" ")[0] || "Agent"}`}
      subtitle="Pick a client to generate an instant pre-approval and loan estimate."
      bgImage={STREET_IMG}
    >
      {status && <Banner status={status.status} canSend={status.can_send} lender={status} />}

      <Tabs defaultValue="clients">
        <TabsList className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 p-1 rounded-xl mb-8 !flex !w-full !flex-nowrap justify-start gap-1 !h-auto overflow-x-auto">
          {[
            ["clients", "My Clients"],
            ["lenders", "My Lenders"],
            ["profile", "Profile"],
          ].map(([v, label]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
              className="flex-1 whitespace-nowrap data-[state=active]:bg-[#ff3b30] data-[state=active]:text-white text-white/60 rounded-lg px-2 sm:px-4 text-xs sm:text-sm">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="clients"><ClientsTab /></TabsContent>
        <TabsContent value="lenders"><MyLendersTab /></TabsContent>
        <TabsContent value="profile"><ProfileTab /></TabsContent>
      </Tabs>
    </Shell>
  );
}
